import { supabase } from "@/integrations/supabase/client";

/**
 * Transforms an array of tag IDs into their corresponding tag names
 * @param tagIds Array of tag IDs to transform
 * @returns Promise with array of tag names
 */
export const transformTagIdsToNames = async (
  tagIds: string[]
): Promise<string[]> => {
  if (!tagIds || tagIds.length === 0) {
    return [];
  }

  try {
    const { data, error } = await supabase
      .from("menu_tags")
      .select("id, name")
      .in("id", tagIds);

    if (error) {
      console.error("Error fetching tag names:", error);
      return [];
    }

    // Create a map of ID to name for quick lookup
    const tagMap = new Map();
    data.forEach((tag) => {
      tagMap.set(tag.id, tag.name);
    });

    // Transform the IDs to names while preserving order
    return tagIds.map((id) => tagMap.get(id) || id);
  } catch (error) {
    console.error("Error transforming tag IDs to names:", error);
    return [];
  }
};

/**
 * Cache for tag ID to name mapping to reduce API calls
 */
const tagCache = new Map<string, string>();

/**
 * Transforms tag IDs to names with caching for better performance
 * @param tagIds Array of tag IDs to transform
 * @returns Promise with array of tag names
 */
export const getTagNames = async (tagIds: string[]): Promise<string[]> => {
  if (!tagIds || tagIds.length === 0) {
    return [];
  }

  // Check which IDs we need to fetch
  const uncachedIds = tagIds.filter((id) => !tagCache.has(id));

  // If we have uncached IDs, fetch them
  if (uncachedIds.length > 0) {
    try {
      // First try to fetch by ID
      const { data: idData, error: idError } = await supabase
        .from("menu_tags")
        .select("id, name")
        .in("id", uncachedIds);

      if (!idError && idData) {
        // Update cache with new data
        idData.forEach((tag) => {
          tagCache.set(tag.id, tag.name);
        });
      }

      // Then try to fetch by name for any remaining uncached items
      const remainingIds = uncachedIds.filter((id) => !tagCache.has(id));
      if (remainingIds.length > 0) {
        const { data: nameData, error: nameError } = await supabase
          .from("menu_tags")
          .select("id, name")
          .in("name", remainingIds);

        if (!nameError && nameData) {
          // Update cache with new data
          nameData.forEach((tag) => {
            tagCache.set(tag.id, tag.name);
          });
        }
      }
    } catch (error) {
      console.error("Error fetching tag names for cache:", error);
    }
  }

  // Return names from cache, falling back to IDs if not found
  return tagIds.map((id) => tagCache.get(id) || id);
};

/**
 * Clear the tag cache when needed (e.g., after adding/updating tags)
 */
export const clearTagCache = () => {
  tagCache.clear();
};

export function getTagDisplayName(tagId: string): string {
  return `tags.${tagId}`; // Returns translation key for the tag
}
